// Panther Terminal: command bar, screen switch, venue picker, then the
// venue's Dashboard or All Stocks screen. The board refreshes itself every
// minute from the edge-cached API; the data underneath moves every 30 minutes.

import { useCallback, useEffect, useMemo, useReducer, useState } from "react";

import { chainLabel, explorerUrl, fetchBoard } from "./api";
import { boardLoad, EMPTY_LOAD } from "./board-load";
import { AllVenues } from "./components/AllVenues";
import { ClassSwitch } from "./components/ClassSwitch";
import { Dashboard } from "./components/Dashboard";
import { HistoryPanel } from "./components/HistoryPanel";
import { Heatmap } from "./components/Heatmap";
import { ScreenPanel, ScreenTabs, useScreenTabsIds } from "./components/ScreenTabs";
import { StocksTable, type StockRow } from "./components/StocksTable";
import { TokenPanel } from "./components/TokenPanel";
import { ago, tokenName, utcTime } from "./format";
import { parseTokenId } from "./token-api";

const REFRESH_MS = 60_000;

interface VenueOption {
  id: string;
  label: string;
  group: string;
  /** Command-bar code for the venue. */
  ticker: string;
}

export const VENUES: VenueOption[] = [
  { id: "robinhood", label: "Robinhood", group: "Chains", ticker: "RHC" },
  { id: "coinbase", label: "Coinbase", group: "Chains", ticker: "CBSE" },
  { id: "ondo", label: "Ondo", group: "Issuing platforms", ticker: "ONDO" },
  { id: "xstocks", label: "xStocks", group: "Issuing platforms", ticker: "XSTK" },
  { id: "bstocks", label: "bStocks", group: "Issuing platforms", ticker: "BSTK" },
  { id: "securitize", label: "Securitize", group: "Issuing platforms", ticker: "SECZ" },
  { id: "dinari", label: "Dinari", group: "Issuing platforms", ticker: "DINR" },
  { id: "figure", label: "Figure", group: "Issuing platforms", ticker: "FIGR" },
  { id: "reality", label: "Reality", group: "Issuing platforms", ticker: "RLTY" },
  { id: "openingbell", label: "Opening Bell", group: "Issuing platforms", ticker: "OBEL" },
  { id: "wisdomtree", label: "WisdomTree", group: "Issuing platforms", ticker: "WSDT" },
];

/** Picker value for the market-wide overview; the default without ?venue=. */
const ALL = "all";
const ALL_OPTION: VenueOption = { id: ALL, label: "All venues", group: "", ticker: "ALL" };

/** "classes" (the cross-class league tables, C-7) exists on the all-venues view only. */
type Screen = "dashboard" | "stocks" | "heatmap" | "classes";

const VENUE_SCREENS = [
  ["dashboard", "Dashboard"],
  ["stocks", "All stocks"],
  ["heatmap", "Heatmap"],
] as const satisfies ReadonlyArray<readonly [Screen, string]>;

/** The screen the address asks for; null without one, so each view opens on its own default. */
function readScreen(): Screen | null {
  const screen = new URLSearchParams(location.search).get("screen");
  return screen === "stocks" || screen === "heatmap" || screen === "dashboard" || screen === "classes" ? screen : null;
}

function readVenue(): string {
  const id = new URLSearchParams(location.search).get("venue");
  return VENUES.some((v) => v.id === id) ? id! : ALL;
}

/** ?token=<chain:address> opens the token panel on load (deep link). */
function readToken(): string | null {
  return parseTokenId(new URLSearchParams(location.search).get("token"))?.id ?? null;
}

/** Keeps the address bar shareable: ?venue=ondo&screen=stocks&token=ethereum:0x…. */
function writeParam(name: string, value: string | null): void {
  const url = new URL(location.href);
  if (value === null) url.searchParams.delete(name);
  else url.searchParams.set(name, value);
  history.replaceState(null, "", url);
}

export function App() {
  const [venueId, setVenueId] = useState(readVenue);
  const [screen, setScreen] = useState<Screen | null>(readScreen);
  const [selectedToken, setSelectedToken] = useState<string | null>(readToken);
  const [{ board, error, pending }, dispatch] = useReducer(boardLoad, EMPTY_LOAD);
  const [, setTick] = useState(0);
  const venue = VENUES.find((v) => v.id === venueId) ?? ALL_OPTION;
  const tabIds = useScreenTabsIds();

  useEffect(() => {
    const controller = new AbortController();
    dispatch({ type: "reset" });
    if (venueId === ALL) return;
    const load = () =>
      fetchBoard(venueId, controller.signal)
        .then((next) => dispatch({ type: "loaded", board: next }))
        .catch((e: unknown) => {
          if (controller.signal.aborted) return;
          dispatch({ type: "failed", error: e });
        });
    void load();
    const timer = setInterval(() => {
      void load();
      setTick((t) => t + 1);
    }, REFRESH_MS);
    return () => {
      controller.abort();
      clearInterval(timer);
    };
  }, [venueId]);

  function selectVenue(id: string): void {
    setVenueId(id);
    writeParam("venue", id === ALL ? null : id);
  }

  function switchScreen(next: Screen): void {
    setScreen(next);
    writeParam("screen", next);
  }

  // Stable identities: the panel's Escape listener and the grids' column
  // definitions depend on these.
  const openToken = useCallback((id: string) => {
    setSelectedToken(id);
    writeParam("token", id);
  }, []);
  const closeToken = useCallback(() => {
    setSelectedToken(null);
    writeParam("token", null);
  }, []);
  const openRow = useCallback((row: StockRow) => openToken(row.id), [openToken]);

  const rows = useMemo<StockRow[]>(() => {
    if (!board) return [];
    const cap = board.totals.market_cap;
    return board.tokens.map((t) => ({
      id: t.id,
      symbol: t.symbol,
      name: tokenName(t.name),
      address: t.address,
      explorerUrl: explorerUrl(t.chain, t.address),
      chains: (t.chains ?? [t.chain]).map(chainLabel).join(", "),
      halted: t.halted,
      price: t.price,
      priceNote: t.price_note ?? null,
      change: t.change_24h.price,
      mcap: t.market_cap,
      supply: t.supply,
      share: cap && t.market_cap !== null ? t.market_cap / cap : null,
      volume: t.volume_usd,
      mintBurn: t.mint_burn_usd,
      netFlow: t.net_flow_1d_usd ?? null,
      netFlow7d: t.net_flow_7d_usd ?? null,
      netFlow30d: t.net_flow_30d_usd ?? null,
      flowMethod: t.flow_method ?? null,
    }));
  }, [board]);

  const groups = [...new Set(VENUES.map((v) => v.group))];
  // A single venue opens on its dashboard and has no cross-class screen; the all-venues view opens on All classes.
  const venueScreen = screen === null || screen === "classes" ? "dashboard" : screen;

  return (
    <div className="terminal">
      <p className="command-bar">
        <span aria-hidden className="cursor" />
        <span>
          {venue.ticker} &lt;EQUITY&gt; <em>STOCK TOKENS</em> &lt;GO&gt;
        </span>
        <span>PANTHER/TERMINAL</span>
      </p>
      <main>
        <header className="screen-head">
          <div className="screen-title">
            <h1 className="sr-only">{venueId === ALL ? "Tokenized assets" : "Tokenized stocks"}</h1>
            <ClassSwitch current={venueId === ALL && (screen === null || screen === "classes") ? null : "stocks"} />
          </div>
          <span className="head-links">
            <a className="head-link" href="/tokenization/methodology">
              How we count
            </a>
            <a className="head-link" href="https://api.pantherterminal.com/v1" rel="noreferrer" target="_blank">
              API
            </a>
          </span>
        </header>

        <div className="venue-bar">
          <label className="venue-picker">
            <span className="venue-picker-label">Venue</span>
            <select
              className="venue-select"
              onChange={(e) => selectVenue(e.target.value)}
              value={venueId}
            >
              <option value={ALL}>{ALL_OPTION.label}</option>
              {groups.map((group) => (
                <optgroup key={group} label={group}>
                  {VENUES.filter((v) => v.group === group).map((v) => (
                    <option key={v.id} value={v.id}>
                      {v.label}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
          </label>
        </div>

        {venueId === ALL ? (
          <>
          <HistoryPanel chartTitle="Tokenized stocks, market cap across venues, USD" subject={{ kind: "class", classApiId: "stock" }} title="Value over time" />
          <AllVenues onScreen={switchScreen} onSelectToken={openToken} onSelectVenue={selectVenue} screen={screen} venues={VENUES} />
          </>
        ) : (
        <>
        <p className="venue-meta">
          {board ? (
            <>
              <strong>{board.totals.tokens}</strong> {venueId === "robinhood" ? "catalog tokens" : "tokens"} on{" "}
              {board.venue.chains ?? venue.label} · as of{" "}
              <strong>{utcTime(board.as_of)}</strong> · updated {ago(board.generated_at)} · refreshes every 30 min
            </>
          ) : pending ? (
            <>First collection for {venue.label} is in progress. This board appears within a few minutes.</>
          ) : error ? (
            <span className="error">Data unavailable right now: {error}</span>
          ) : (
            "Loading…"
          )}
        </p>

        <ScreenTabs current={venueScreen} ids={tabIds} label="Venue screen" onSelect={switchScreen} tabs={VENUE_SCREENS} />

        <ScreenPanel current={venueScreen} ids={tabIds}>
          {board ? venueScreen === "dashboard" ? <Dashboard board={board} /> : venueScreen === "heatmap" ? <Heatmap board={board} onSelect={openToken} /> : <StocksTable csvName={`${venueId}-stocks.csv`} onSelect={openRow} rows={rows} /> : null}
        </ScreenPanel>
        </>
        )}
      </main>
      <footer className="footer">
        Panther Research · Data from issuer catalogs and on-chain reads, refreshed every 30 minutes. Not investment advice.
      </footer>
      <TokenPanel onClose={closeToken} onOpen={openToken} tokenId={selectedToken} />
    </div>
  );
}
